/* eslint-disable */
import { useState } from "react";
import { Box, Container, Button, IconButton, Tooltip } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IProgress,
  ISessionProps,
  OSType,
  SDKTestType,
  SessionStatus,
} from "../main/components/IProgress";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import { Typography, Note } from "@appsflyer/fe-ui-core";
import transition from "../main/components/transition";
import {
  DJSimpleTextBlock,
} from "../main/components/codeblock";
import { amplitudeTrack_continue } from "../services/utils";
import AmplitudeLink from "../services/AmplitudeLink";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text).then(() => {
    console.log("Copied to clipboard:", text);
  });
};

function stringifyWithPlaceholders(obj: Record<string, any>, indent = 2) {
  const indentStr = (level: number) => " ".repeat(level * indent);

  function replacer(key: string, value: any, level: number): string {
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      const entries = Object.entries(value)
        .map(
          ([k, v]) =>
            `${indentStr(level + 1)}"${k}": ${replacer(k, v, level + 1)}`
        )
        .join(",\n");
      return `{\n${entries}\n${indentStr(level)}}`;
    } else if (typeof value === "object" && Array.isArray(value)) {
      const items = value
        .map(
          (item) => `${indentStr(level + 1)}${replacer("", item, level + 1)}`
        )
        .join(",\n");
      return `[\n${items}\n${indentStr(level)}]`;
    } else if (value === "<<VALUE>>") {
      return value; // no quotes
    } else if (value === "YOUR_MONETIZATION_NETWORK") {
      return "<<VALUE>>"; // "YOUR_MONETIZATION_NETWORK" turn into <<VALUE>>
    } else if (typeof value === "string") {
      return `"${value}"`;
    } else {
      return String(value);
    }
  }

  return `{\n${indentStr(1)}"event": "adrevenue_sdk",\n${indentStr(
    1
  )}"payload": ${replacer("payload", obj.payload, 1)}\n}`;
}

const Step4_Android = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const modifiedUrl = `POST:https://<<RANDOM-STRING>>-adrevenue.appsflyersdk.com/api/v2/generic/<<SDK_VERSION>>/android?app_id=${location.state?.appID}`;
  const modifiedUrlSearch = `${location.state.os}?app_id=${location.state?.appID}`;
  const eventParams = location.state?.eventParams ?? {};
  const safePayload = Object.entries(eventParams).reduce(
    (acc, [key, val]) => {
      acc[key] = val ? val : `<<VALUE>>`;
      return acc;
    },
    {} as Record<string, any>
  );

  const code = stringifyWithPlaceholders({
    event: "adrevenue_sdk",
    payload: safePayload,
  });

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h3">
          {t("ip_InAppResults_android_eventPreparedLabel")}
        </Typography>
        <span>
          {t("ip_InAppResults_android_searchLogs")} <code>preparing data:</code>{" "}
          {t("ip_InAppResults_android_holdsString")}
        </span>
        <Box position="relative">
          {/* Copy Button for Right Side (New Content) */}
          <Box
            position="absolute"
            top="4px" // Position at the top of the right side
            left="675px" // Align to the right
            style={{ color: "white" }}
          >
            <Tooltip placement="top" title="Copy">
              <IconButton
                size="small"
                color="primary"
                aria-label="Small outlined icon button"
                onClick={() => copyToClipboard(code)}
                style={{ padding: "3px", border: "1px solid" }} // Change the border color here                      }}
              >
                <ContentCopyRoundedIcon
                  fontSize="small"
                  style={{ cursor: "pointer", fontSize: "14px" }} // Custom font size
                />
              </IconButton>
            </Tooltip>
          </Box>
          <DJSimpleTextBlock
            codePrimary={code}
            showLineNumbers={true}
            language="java"
          />
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="h3">
          {t("ip_InAppResults_ios_eventSentLabel")}
        </Typography>
        <span>{t("ip_InAppResults_android_holdsStringsLabel")} </span>
        <Box sx={{ mb: 1 }}>
          <Box position="relative">
            {/* Copy Button for Right Side (New Content) */}
            <Box
              position="absolute"
              top="4px" // Position at the top of the right side
              left="675px" // Align to the right
              style={{ color: "white" }}
            >
              <Tooltip placement="top" title="Copy">
                <IconButton
                  size="small"
                  color="primary"
                  aria-label="Small outlined icon button"
                  onClick={() => copyToClipboard(modifiedUrlSearch)}
                  style={{ padding: "3px", border: "1px solid" }} // Change the border color here                      }}
                >
                  <ContentCopyRoundedIcon
                    fontSize="small"
                    style={{ cursor: "pointer", fontSize: "14px" }} // Custom font size
                  />
                </IconButton>
              </Tooltip>
            </Box>
            <DJSimpleTextBlock
              codePrimary={modifiedUrlSearch}
              showLineNumbers={true}
            />
          </Box>
        </Box>
        <span>{t("ip_InAppResults_android_holdsStringsLabel_example")} </span>
        <Box position="relative">
          {/* Copy Button for Right Side (New Content) */}
          <Box
            position="absolute"
            top="4px" // Position at the top of the right side
            left="675px" // Align to the right
            style={{ color: "white" }}
          >
            <Tooltip placement="top" title="Copy">
              <IconButton
                size="small"
                color="primary"
                aria-label="Small outlined icon button"
                onClick={() => copyToClipboard(modifiedUrl)}
                style={{ padding: "3px", border: "1px solid" }} // Change the border color here                      }}
              >
                <ContentCopyRoundedIcon
                  fontSize="small"
                  style={{ cursor: "pointer", fontSize: "14px" }} // Custom font size
                />
              </IconButton>
            </Tooltip>
          </Box>
          <DJSimpleTextBlock
            codePrimary={modifiedUrl}
            showLineNumbers={false}
          />
        </Box>
        <Note title={""} sx={{ mt: 2 }} maxWidth={700}>
        <Typography>
            <Trans i18nKey="adRevenue_verify_integration_1" />
            <AmplitudeLink
              href="https://support.appsflyer.com/hc/en-us/articles/360001559405-Testing-the-SDK-integration-for-marketers#view-live-events"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("adRevenue_verify_integration_link")}
            </AmplitudeLink>
            <Trans i18nKey="adRevenue_verify_integration_2" />
          </Typography>
        </Note>
      </Box>
    </>
  );
};

const Step4_iOS = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const modifiedUrl = `https://<<RANDOM_STRING>>-adrevenue.appsflyersdk.com/api/v2/generic/<<SDK_VERSION>>/ios?app_id=${location.state?.appId}`;
  const modifiedUrlSearch = `${location.state.os}?app_id=${location.state?.appID}`;
  const eventParams = location.state?.eventParams ?? {};
  const safePayload = Object.entries(eventParams).reduce(
    (acc, [key, val]) => {
      acc[key] = val ? val : `<<VALUE>>`;
      return acc;
    },
    {} as Record<string, any>
  );
  const code = stringifyWithPlaceholders({
    event: "adrevenue_sdk",
    payload: safePayload,
  });

  return (
    <>
      <>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h3">
            {t("ip_InAppResults_android_eventPreparedLabel")}
          </Typography>
          <span>
            {t("ip_InAppResults_android_searchLogs")}{" "}
            <code>preparing data:</code>{" "}
            {t("ip_InAppResults_android_holdsString")}
          </span>
          <Box position="relative">
            {/* Copy Button for Right Side (New Content) */}
            <Box
              position="absolute"
              top="4px" // Position at the top of the right side
              left="675px" // Align to the right
              style={{ color: "white" }}
            >
              <Tooltip placement="top" title="Copy">
                <IconButton
                  size="small"
                  color="primary"
                  aria-label="Small outlined icon button"
                  onClick={() => copyToClipboard(code)}
                  style={{ padding: "3px", border: "1px solid" }} // Change the border color here                      }}
                >
                  <ContentCopyRoundedIcon
                    fontSize="small"
                    style={{ cursor: "pointer", fontSize: "14px" }} // Custom font size
                  />
                </IconButton>
              </Tooltip>
            </Box>
            <DJSimpleTextBlock
              codePrimary={code}
              showLineNumbers={true}
              language="swift"
            />
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="h3">
            {t("ip_InAppResults_ios_eventSentLabel")}
          </Typography>
          <span>{t("ip_InAppResults_android_holdsStringsLabel")} </span>
          <Box position="relative" sx={{ mb: 1 }}>
            {/* Copy Button for Right Side (New Content) */}
            <Box
              position="absolute"
              top="4px" // Position at the top of the right side
              left="675px" // Align to the right
              style={{ color: "white" }}
            >
              <Tooltip placement="top" title="Copy">
                <IconButton
                  size="small"
                  color="primary"
                  aria-label="Small outlined icon button"
                  onClick={() => copyToClipboard(modifiedUrlSearch)}
                  style={{ padding: "3px", border: "1px solid" }} // Change the border color here                      }}
                >
                  <ContentCopyRoundedIcon
                    fontSize="small"
                    style={{ cursor: "pointer", fontSize: "14px" }} // Custom font size
                  />
                </IconButton>
              </Tooltip>
            </Box>
            <DJSimpleTextBlock
              codePrimary={modifiedUrlSearch}
              showLineNumbers={false}
            />
          </Box>
          <span>{t("ip_InAppResults_android_holdsStringsLabel_example")} </span>
          <Box position="relative">
            {/* Copy Button for Right Side (New Content) */}
            <Box
              position="absolute"
              top="4px" // Position at the top of the right side
              left="675px" // Align to the right
              style={{ color: "white" }}
            >
              <Tooltip placement="top" title="Copy">
                <IconButton
                  size="small"
                  color="primary"
                  aria-label="Small outlined icon button"
                  onClick={() => copyToClipboard(modifiedUrl)}
                  style={{ padding: "3px", border: "1px solid" }} // Change the border color here                      }}
                >
                  <ContentCopyRoundedIcon
                    fontSize="small"
                    style={{ cursor: "pointer", fontSize: "14px" }} // Custom font size
                  />
                </IconButton>
              </Tooltip>
            </Box>
            <DJSimpleTextBlock
              codePrimary={modifiedUrl}
              showLineNumbers={false}
            />
          </Box>
          <Note title={""} sx={{ mt: 2 }} maxWidth={700}>
            <Typography>
              <Trans i18nKey="adRevenue_verify_integration_1" />
              <AmplitudeLink
                href="https://support.appsflyer.com/hc/en-us/articles/360001559405-Testing-the-SDK-integration-for-marketers#view-live-events"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("adRevenue_verify_integration_link")}
              </AmplitudeLink>
              <Trans i18nKey="adRevenue_verify_integration_2" />
            </Typography>
          </Note>
        </Box>
      </>
    </>
  );
};

const AdRevenueTestManual = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  function finishProcess() {
    const progressArr: IProgress[] = location.state?.progress.slice();
    progressArr.push({
      id: "startsdk_testevents",
      text: "progress_startsdk_testevents",
      order: 7,
    });
    
    const progressState: ISessionProps = {
      ...location.state,
      currentPage: location.state?.currentPage + 1,
      sessionStatus: SessionStatus.Success,
      completionStatus: true,
    };

    // // send gtag for manual testing success
    // gtag("event", "manual_test_success_fixed", {
    //   app_name: "devjourney",
    //   screen_name: "TestInappIntegration",
    // });
    amplitudeTrack_continue(location.state);

    navigate("/finish", { state: progressState });
  }

  const steps = [
    {
      label: t("ip_TestInAppIntegration_ios_stepOneHeading"),
      component: (
        <>
          {location.state?.sdkTestType === SDKTestType.Manual ? (
            <>
              <ul>
                <li>
                  <Typography variant="body1">
                    {t("ip_TestInAppIntegration_ios_stepOneContent")}
                    <br />
                    {t("ip_TestInAppIntegration_ios_stepOneContent_b")}
                    <Trans i18nKey="adRevenue_event_snippet_title" />
                    {t("ip_TestInAppIntegration_ios_stepOneContent_d")}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <span style={{ fontWeight: "bold" }}>
                      {t("startSDK_TestSDKIntegration_android_Step2") + ": "}
                    </span>
                    <AmplitudeLink
                      target="_blank"
                      href={
                        location.state?.os === OSType.Android
                          ? "https://dev.appsflyer.com/hc/docs/integrate-android-sdk#enabling-debug-mode"
                          : "https://dev.appsflyer.com/hc/docs/integrate-ios-sdk#enabling-debug-mode"
                      }
                    >
                      {t("startSDK_TestSDKIntegration_android_EnableDebugMode")}
                    </AmplitudeLink>
                  </Typography>
                </li>
              </ul>
            </>
          ) : (
            <>
              <Typography variant="body1">
                {t("ip_TestInAppIntegration_ios_stepOneContent")}
                <br />
                {t("ip_TestInAppIntegration_ios_stepOneContent_b")}
                <b>{t("ip_TestInAppIntegration_ios_stepOneContent_c")}</b>
                {t("ip_TestInAppIntegration_ios_stepOneContent_d")}
              </Typography>
            </>
          )}
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: t("adRevenue_test_trigger_step_title"),
      component: (
        <>
          <Typography>
            <Trans i18nKey={"adRevenue_test_trigger_step_desc"}/>
          </Typography>
          {location.state?.os === OSType.iOS &&
            location.state?.deviceType === "IDFA" && (
              <Box mt={2}>
                <Note>
                  <Typography style={{ display: "inline" }}>
                    {t("testConsole_TestType_myDeviceID_iosAtt")}
                  </Typography>
                </Note>
              </Box>
            )}
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
  ];

  if (location.state?.sdkTestType === SDKTestType.Manual) {
    steps.push({
      label: t("ip_InAppResults_ios_stepFourHeading"),
      component: (
        <>
          {location.state?.os === OSType.Android ? (
            <Step4_Android />
          ) : (
            <Step4_iOS />
          )}
        </>
      ),
      shouldDisplayNavigationButtons: true,
    });
  }

  return (
    <Container maxWidth={"lg"}>
      <Box padding={3} style={{ minHeight: "50vh" }}>
        <Typography variant="h1">
          {t("adRevenue_Test_Ad_Revenue_Integration")}
        </Typography>
        <Note title={""} sx={{ mt: 2 }}>
          <Typography>
          <Trans
              i18nKey="adRevenue_beforeYouStart_1"
              components={{
                strong: <strong />,
                em: <em />,
              }}
            />
            <AmplitudeLink
              href="https://hq1.appsflyer.com/revenue-config/overview"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("adRevenue_beforeYouStart_Link_1")}
            </AmplitudeLink>
            <Trans
              i18nKey="adRevenue_beforeYouStart_2"
              components={{
                strong: <strong />,
                em: <em />,
              }}
            />
            <AmplitudeLink
              href="https://support.appsflyer.com/hc/en-us/articles/217490046-ROI360-guide-ad-revenue-attribution#connect-to-ad-revenue-integrated-partners"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("adRevenue_beforeYouStart_Link_2")}
            </AmplitudeLink>
            .
          </Typography>
        </Note>

        <Stepper
          activeStep={activeStep}
          style={{ border: "none" }}
          orientation="vertical"
        >
          {steps.map(({ label, component, shouldDisplayNavigationButtons }) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {component}
                {shouldDisplayNavigationButtons && (
                  <Box sx={{ mt: 2 }}>
                    {activeStep !== 0 && (
                      <Button
                        variant="text"
                        color="primary"
                        onClick={handleBack}
                        // sx={{ mt: 2, mr: 1 }}
                      >
                        {t("general_Back")}
                      </Button>
                    )}
                    {activeStep !== steps.length - 1 && (
                      <Button
                        // sx={{ mt: 2 }}
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                      >
                        {activeStep === steps.length - 1
                          ? t("general_Finish")
                          : t("general_Next")}
                      </Button>
                    )}
                  </Box>
                )}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>
      <Box
        display={"flex"}
        width={"100%"}
        justifyContent="space-between"
        alignSelf="end"
        justifySelf="end"
        marginTop={10}
        textAlign="end"
      >
        {" "}
        <Button
          variant="outlined"
          size="medium"
          color="secondary"
          style={{ marginRight: "5px" }}
          onClick={() => navigate(-1)}
        >
          {t("general_Back")}
        </Button>
        <Button
          variant="contained"
          size="medium"
          color="primary"
          onClick={() => finishProcess()}
          disabled={activeStep != steps.length - 1}
        >
          {t("ip_InAppResults_android_doneButton")}
        </Button>
      </Box>
    </Container>
  );
};

export default transition(AdRevenueTestManual);
